import React from "react";
import { Activity, Target, TrendingUp, AlertTriangle, ShieldCheck } from "lucide-react";
import BgOrbs from "./BgOrbs";

const CALLOUTS = [
  { icon: Activity, title: "Completion tracking", body: "Know exactly where every learner is, in real time." },
  { icon: Target, title: "Topic-level weak spots", body: "Pinpoint which concepts aren't landing." },
  { icon: TrendingUp, title: "Engagement signals", body: "See attention, pacing, and drop-off patterns." },
  { icon: AlertTriangle, title: "Intervention recommendations", body: "Act on AI-surfaced next-best actions." },
];

export default function Analytics() {
  return (
    <section
      id="analytics"
      data-testid="analytics-section"
      className="relative min-h-screen flex items-center py-16 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #ffffff 0%, #f0fdfa 45%, #eff6ff 100%)",
      }}
    >
      <BgOrbs variant="teal" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-[1fr_1.25fr] gap-14 items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-700">
              Trainer dashboard
            </div>
            <h2 className="mt-3 text-[34px] sm:text-[44px] leading-[1.08] font-extrabold tracking-tight text-slate-900 text-balance">
              Built for trainers, not just learners.
            </h2>
            <p className="mt-4 text-[16.5px] text-slate-600 leading-relaxed max-w-md">
              See what's working, who is stuck, and where intervention matters.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {CALLOUTS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="flex gap-3 rounded-xl bg-white/70 backdrop-blur border border-slate-200 px-3.5 py-3 shadow-sm"
                    data-testid={`analytics-callout-${i}`}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-blue-500 text-white flex-shrink-0">
                      <Icon size={17} strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-slate-900">
                        {c.title}
                      </div>
                      <div className="text-[12px] text-slate-600 leading-snug">
                        {c.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="relative w-full min-w-0">
            <div
              className="bg-white rounded-2xl sm:rounded-[24px] border border-slate-200 shadow-[0_40px_100px_-40px_rgba(15,23,42,0.25)] overflow-hidden max-w-full"
              data-testid="analytics-dashboard-mockup"
            >
              {/* Top bar */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 py-2.5 sm:py-3 border-b border-slate-100 bg-slate-50/60">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-rose-400" />
                  <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-emerald-400" />
                  <span className="ml-1 sm:ml-3 truncate text-[11px] sm:text-[12.5px] font-bold text-slate-800">
                    ai-trainer / dashboard
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-[11px] font-semibold text-slate-500">
                  <span className="rounded-md bg-white px-2 py-0.5 border border-slate-200">
                    Last 7 days
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 min-[560px]:grid-cols-[minmax(136px,_180px)_1fr] min-w-0">
                {/* Sidebar — horizontal chips on narrow viewports */}
                <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto overscroll-x-contain border-b border-slate-100 bg-slate-50/40 px-2 py-2.5 min-[560px]:flex-col min-[560px]:overflow-visible min-[560px]:border-b-0 min-[560px]:border-r min-[560px]:px-4 min-[560px]:py-4 [-webkit-overflow-scrolling:touch]">
                  <div className="hidden min-[560px]:block px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 min-[560px]:px-0">
                    Workspace
                  </div>
                  {[
                    { label: "Overview", active: true },
                    { label: "Sessions" },
                    { label: "Learners" },
                    { label: "Insights" },
                    { label: "Interventions" },
                    { label: "Settings" },
                  ].map((i) => (
                    <div
                      key={i.label}
                      className={`shrink-0 rounded-lg px-3 py-2 text-[11.5px] min-[560px]:text-[12.5px] font-medium min-[560px]:mx-0 min-[560px]:mb-1 min-[560px]:w-auto ${
                        i.active
                          ? "bg-white text-slate-900 border border-slate-100 shadow-sm"
                          : "text-slate-500 hover:text-slate-800 cursor-pointer whitespace-nowrap"
                      }`}
                    >
                      {i.label}
                    </div>
                  ))}
                </div>

                {/* Main */}
                <div className="space-y-3 p-3 sm:p-5 sm:space-y-4 min-w-0">
                  {/* Stat row */}
                  <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 min-[560px]:grid-cols-3">
                    {[
                      { label: "Completion rate", value: "87%", trend: "+12%", color: "text-emerald-600" },
                      { label: "Active learners", value: "1,246", trend: "+134", color: "text-blue-600" },
                      { label: "Avg. session time", value: "18m", trend: "+4m", color: "text-violet-600" },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="rounded-xl border border-slate-100 bg-white p-3.5"
                      >
                        <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          {s.label}
                        </div>
                        <div className="mt-1 text-[20px] sm:text-[24px] font-extrabold text-slate-900 leading-none tabular-nums">
                          {s.value}
                        </div>
                        <div
                          className={`mt-1 text-[11px] font-semibold ${s.color}`}
                        >
                          {s.trend} vs last week
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Chart */}
                  <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-blue-50/50 to-violet-50/50 p-3 sm:p-4">
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                        Completion by day
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10.5px] font-semibold text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-blue-500" /> Sessions
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-violet-400" /> Completions
                        </span>
                      </div>
                    </div>
                    <div className="flex items-end gap-2 h-24">
                      {[60, 72, 66, 82, 90, 95, 88].map((h, i) => (
                        <div key={i} className="flex-1 relative">
                          <div
                            className="rounded-t-md bar-grow"
                            style={{
                              height: `${h}%`,
                              background:
                                "linear-gradient(to top, #3b82f6, #8b5cf6)",
                              animationDelay: `${i * 0.08}s`,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 grid grid-cols-7 gap-2 text-[10px] text-slate-500 font-medium">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                        (d) => (
                          <div key={d} className="text-center">
                            {d}
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Intervention card */}
                  <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-3.5 shadow-sm min-[520px]:flex-row min-[520px]:items-center sm:px-4">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-white shadow-sm">
                        <ShieldCheck size={18} className="text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[12px] font-bold leading-snug text-amber-900 sm:text-[13px]">
                          12 learners need support in policy comprehension
                        </div>
                        <div className="mt-0.5 text-[11px] text-amber-800 sm:text-[11.5px]">
                          Recommended action · send a 10-minute reinforcement session
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid="intervention-review-btn"
                      type="button"
                      className="w-full shrink-0 rounded-full px-4 py-2.5 text-[12px] font-semibold text-white shadow-sm min-[520px]:w-auto min-[520px]:self-center min-[520px]:whitespace-nowrap"
                      style={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                      }}
                    >
                      Review learners
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating insight chip */}
            <div className="hidden md:flex absolute -top-4 -right-4 items-center gap-2 bg-white border border-slate-200 rounded-full shadow-lg px-3 py-1.5">
              <span className="flex w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] font-semibold text-slate-700">
                Completion ↑ 14% this week
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
